import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, PackageSearch, ScanLine, X, Package, ChevronLeft, ChevronRight, Sparkles, Plus, Trash2 } from 'lucide-react';
import { DragEndEvent } from '@dnd-kit/core';
import RichTextEditor from '@/components/RichTextEditor';
import ImageManagerDialog from '@/components/ImageManagerDialog';
import ImageStage from '@/components/ImageStage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { getProductById, updateProduct, supabase, ProductItem, deleteImageFromStorage } from '@/lib/supabase';
import { useScrollLock } from '@/hooks/useScrollLock';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useSession } from '@/context/SessionContext';
import { cn } from '@/lib/utils';
import QuickProductForm from './QuickProductForm';

interface EditProductSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
  onUpdated?: () => void;
}

type QuantityKind = 'mass' | 'liquid';

const stripHtmlTags = (html?: string): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const PRODUCT_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'groceries', label: 'Almacén' },
  { value: 'electronics', label: 'Electrónica' },
  { value: 'clothing', label: 'Ropa' },
  { value: 'book', label: 'Libro' },
  { value: 'vehicle', label: 'Vehículo' },
  { value: 'art', label: 'Arte' },
  { value: 'diversion', label: 'Diversión' },
  { value: 'propiedades', label: 'Propiedades' },
  { value: 'tours', label: 'Excursiones' },
  { value: 'clases_particulares', label: 'Clases particulares' },
];

const GROCERIES_SUBCATEGORY_OPTIONS = [
  { value: 'bebidas', label: 'Bebidas' },
  { value: 'bebidas_con_alcohol', label: 'Bebidas con alcohol' },
  { value: 'comestibles', label: 'Comestibles' },
  { value: 'golosinas', label: 'Golosinas' },
  { value: 'lacteos', label: 'Lácteos' },
  { value: 'congelados', label: 'Congelados' },
  { value: 'panaderia', label: 'Panadería' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'cuidado_personal', label: 'Cuidado personal' },
  { value: 'mascotas', label: 'Mascotas' },
  { value: 'otros', label: 'Otros' },
];

const parseContent = (content?: string | null): { value: string; unit: string; kind: QuantityKind } | null => {
  if (!content) return null;
  const match = String(content).trim().match(/^(\d+(?:[\.,]\d+)?)\s*(g|gr|kg|ml|l)$/i);
  if (!match) return null;
  let unit = match[2].toLowerCase();
  // Normalize "gr" to "gr" for UI consistency (buttons show "gr" not "g")
  if (unit === 'g') unit = 'gr';
  return {
    value: match[1].replace(',', '.'),
    unit,
    kind: unit === 'ml' || unit === 'l' ? 'liquid' : 'mass',
  };
};

// Product Preview Card
interface ProductPreviewCardProps {
  imageUrl?: string | null;
  images?: string[];
  title: string;
  brand: string;
  model: string;
  price: string;
  stock: string;
  description?: string;
  deliveryByMinibid: boolean;
  deliveryLocal: boolean;
  deliveryAgree: boolean;
}

const ProductPreviewCard: React.FC<ProductPreviewCardProps> = ({
  imageUrl,
  images,
  title,
  brand,
  model,
  price,
  stock,
  description,
  deliveryByMinibid,
  deliveryLocal,
  deliveryAgree,
}) => {
  const [imageIndex, setImageIndex] = useState(0);
  
  const allImages = images && images.length > 0 ? images : (imageUrl ? [imageUrl] : []);
  const currentImage = allImages.length > 0 ? allImages[imageIndex] : null;
  
  const handlePrevImage = () => {
    setImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  
  const handleNextImage = () => {
    setImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };
  
  return (
    <Card className="transition-all duration-300 transform-gpu flex flex-col overflow-hidden shadow-[0_4px_14px_hsl(var(--primary)/0.05),0_0_0_1px_hsl(var(--primary)/0.06)] dark:shadow-[0_6px_18px_hsl(var(--primary)/0.08),0_0_0_1px_hsl(var(--primary)/0.09)]">
      <div className="relative bg-primary/10 aspect-square flex items-center justify-center overflow-hidden flex-shrink-0 group">
        {currentImage ? (
          <>
            <img
              src={currentImage}
              alt={title || 'Producto'}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            
            {allImages.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  {imageIndex + 1}/{allImages.length}
                </span>
              </>
            )}
          </>
        ) : (
          <PackageSearch className="h-12 w-12 text-primary/30" />
        )}
      </div>

      <CardHeader className="pb-2 flex-1 pt-3">
        <CardTitle className="text-sm line-clamp-2">
          {title || 'Sin título'}
        </CardTitle>
        {(brand || model || description) && (
          <CardDescription className="line-clamp-1 text-xs">
            {stripHtmlTags(description) || [brand, model].filter(Boolean).join(' • ') || 'Sin descripción'}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-2 pt-0 pb-3 mb-2">
        <div className="flex items-center justify-between gap-1">
          {price ? (
            <span className="text-lg font-bold text-primary">
              ${Number(price).toLocaleString('es-AR')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground italic">Sin precio</span>
          )}
          {stock && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-700 dark:text-green-400 font-medium">
              Stock: {stock}
            </span>
          )}
        </div>

        {(deliveryByMinibid || deliveryLocal || deliveryAgree) && (
          <div className="flex flex-wrap gap-1 pt-1">
            {deliveryByMinibid && (
              <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-400">
                ✓ Minibid
              </span>
            )}
            {deliveryLocal && (
              <span className="inline-flex items-center gap-1 rounded-full border border-orange-500/30 bg-orange-500/10 px-2 py-0.5 text-[11px] font-medium text-orange-700 dark:text-orange-400">
                ✓ Retiro Local
              </span>
            )}
            {deliveryAgree && (
              <span className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:text-purple-400">
                ✓ Acordar
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function EditProductSidebar({
  isOpen,
  onClose,
  productId,
  onUpdated,
}: EditProductSidebarProps) {
  const { toast } = useToast();
  const { headerHeight, isDesktop } = useHeaderOffset();
  useScrollLock(isOpen, false);
  const { session } = useSession();

  const [product, setProduct] = useState<ProductItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form fields
  const [barcode, setBarcode] = useState('');
  const [productType, setProductType] = useState('groceries');
  const [groceriesSubcategory, setGroceriesSubcategory] = useState('');
  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  const [quantityKind, setQuantityKind] = useState<QuantityKind>('mass');
  const [quantityValue, setQuantityValue] = useState('');
  const [quantityUnit, setQuantityUnit] = useState<'gr' | 'kg' | 'ml' | 'l'>('gr');
  const [stock, setStock] = useState('');
  const [price, setPrice] = useState('');
  const [model, setModel] = useState('');
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [deliveryByMinibid, setDeliveryByMinibid] = useState(true);
  const [deliveryLocal, setDeliveryLocal] = useState(true);
  const [deliveryAgree, setDeliveryAgree] = useState(true);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);

  // Vehicle fields
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleKilometers, setVehicleKilometers] = useState('');
  const [vehicleFuelType, setVehicleFuelType] = useState('nafta');
  const [vehicleTransmission, setVehicleTransmission] = useState('manual');
  const [vehicleGNC, setVehicleGNC] = useState(false);

  // Electronics fields
  const [electronicsStorage, setElectronicsStorage] = useState('');
  const [electronicsRAM, setElectronicsRAM] = useState('');

  // Load product on open
  useEffect(() => {
    if (!isOpen || !productId) return;

    const loadProduct = async () => {
      setLoading(true);
      try {
        const prod = await getProductById(productId);
        if (!prod) {
          toast({ title: 'Error', description: 'Producto no encontrado', variant: 'destructive' });
          onClose();
          return;
        }
        setProduct(prod);

        // Populate form
        setBarcode(prod.details?.barcode || '');
        setProductType(prod.product_type || 'groceries');
        setGroceriesSubcategory(prod.details?.groceries_subcategory || '');
        setTitle(prod.title || '');
        setBrand(prod.details?.brand || '');
        setDescription(prod.description || '');
        setPrice(String(prod.fixed_sale_price || prod.starting_bid || ''));
        setStock(String(prod.stock || ''));
        setModel(prod.details?.model || '');
        setSize(prod.details?.size || '');
        setColor(prod.details?.color || '');

        // Images
        const images = prod.image_urls || [];
        setProductImages(images);

        // Quantity
        const parsed = parseContent(prod.details?.content);
        if (parsed) {
          setQuantityValue(parsed.value);
          setQuantityUnit(parsed.unit as 'gr' | 'kg' | 'ml' | 'l');
          setQuantityKind(parsed.kind);
        }

        // Delivery
        const shipping = prod.details?.shipping_options || {};
        setDeliveryByMinibid(shipping.minibid_available !== false);
        setDeliveryLocal(shipping.coordinated_local !== false);
        setDeliveryAgree(shipping.to_be_agreed !== false);

        // Vehicle
        if (prod.product_type === 'vehicle' && prod.details?.vehicle) {
          const v = prod.details.vehicle;
          setVehicleYear(String(v.year || ''));
          setVehicleKilometers(String(v.kilometers || ''));
          setVehicleFuelType(v.fuel_type || 'nafta');
          setVehicleTransmission(v.transmission || 'manual');
          setVehicleGNC(v.gnc || false);
        }

        // Electronics
        if (prod.product_type === 'electronics' && prod.details?.electronics) {
          const e = prod.details.electronics;
          setElectronicsStorage(e.storage || '');
          setElectronicsRAM(e.ram || '');
        }
      } catch (error) {
        console.error('Error loading product:', error);
        toast({ title: 'Error', description: 'No se pudo cargar el producto', variant: 'destructive' });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [isOpen, productId, onClose, toast]);

  const handleImageManagerSelect = (urls: string | string[]) => {
    const imageArray = Array.isArray(urls) ? urls : [urls];
    setProductImages((prev) => {
      const newImages = [...prev, ...imageArray];
      return Array.from(new Set(newImages));
    });
    setIsImageManagerOpen(false);
  };

  const handleImageDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setProductImages((prev) => {
      const activeIndex = prev.indexOf(active.id as string);
      const overIndex = prev.indexOf(over.id as string);
      
      if (activeIndex === -1 || overIndex === -1) return prev;

      const newImages = [...prev];
      newImages.splice(activeIndex, 1);
      newImages.splice(overIndex, 0, active.id as string);
      return newImages;
    });
  }, []);

  const handleRemoveImageUrl = useCallback((url: string) => {
    setProductImages((prev) => prev.filter((img) => img !== url));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!product || !title.trim()) {
      toast({ title: 'Error', description: 'El nombre es requerido', variant: 'destructive' });
      return;
    }

    const priceNumber = Number(price);
    if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
      toast({ title: 'Error', description: 'Ingresa un precio válido', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      // Normalize unit: "gr" should be stored as "g"
      const normalizedUnit = quantityUnit === 'gr' ? 'g' : quantityUnit;
      const content = quantityValue.trim() ? `${quantityValue.trim()} ${normalizedUnit}` : null;
      
      // Auto-build title: Brand + Name + (if) Content
      const titleParts = [] as string[];
      if (brand.trim()) titleParts.push(brand.trim());
      if (title.trim()) titleParts.push(title.trim());
      if (content) titleParts.push(content);
      const finalTitle = titleParts.join(' ').trim() || title.trim();
      
      const details: Record<string, any> = {
        ...product.details,
        barcode: barcode || null,
        brand: brand || null,
        model: model || null,
        content,
        description: description || null,
        quantity_kind: quantityKind,
        shipping_options: {
          minibid_available: deliveryByMinibid,
          coordinated_local: deliveryLocal,
          to_be_agreed: deliveryAgree,
        },
        product_images: productImages,
      };

      if (productType === 'groceries' && groceriesSubcategory) {
        details.groceries_subcategory = groceriesSubcategory;
      }

      if (productType === 'clothing') {
        details.size = size || null;
        details.color = color || null;
      }

      if (productType === 'vehicle') {
        details.vehicle = {
          year: vehicleYear ? parseInt(vehicleYear) : null,
          kilometers: vehicleKilometers ? parseInt(vehicleKilometers.replace(/\D/g, '')) : null,
          fuel_type: vehicleFuelType || null,
          transmission: vehicleTransmission || null,
          gnc: vehicleGNC,
        };
      }

      if (productType === 'electronics') {
        details.electronics = {
          storage: electronicsStorage || null,
          ram: electronicsRAM || null,
        };
      }

      // Update product
      await updateProduct(product.id, product.seller_id, {
        title: finalTitle,
        description,
        product_type: productType,
        fixed_sale_price: priceNumber,
        image_urls: productImages,
        main_image_url: productImages[0] || null,
        details,
      });

      // Update stock and images
      const stockNumber = stock.trim() ? Number(stock) : 0;
      await supabase
        .from('products')
        .update({
          stock: Math.max(0, Math.trunc(stockNumber)),
          fixed_sale_price: priceNumber,
          product_images: productImages,
          image_urls: productImages,
          main_image_url: productImages[0] || null,
        })
        .eq('id', product.id);

      toast({ title: 'Éxito', description: 'Producto actualizado correctamente' });
      onUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('Error updating product:', error);
      toast({
        title: 'Error al actualizar',
        description: error?.message || 'No se pudo actualizar el producto',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [product, title, price, stock, barcode, productType, groceriesSubcategory, brand, model, description, quantityValue, quantityUnit, quantityKind, size, color, vehicleYear, vehicleKilometers, vehicleFuelType, vehicleTransmission, vehicleGNC, electronicsStorage, electronicsRAM, deliveryByMinibid, deliveryLocal, deliveryAgree, productImages, toast, onUpdated, onClose]);

  const handleDeleteProduct = useCallback(async () => {
    if (!product || !session?.user?.id) {
      toast({ title: 'Error', description: 'No se puede eliminar el producto', variant: 'destructive' });
      return;
    }

    setDeleting(true);
    try {
      // Delete images from storage
      for (const url of productImages) {
        try {
          await deleteImageFromStorage(url);
        } catch (err) {
          console.warn('Error deleting image:', err);
        }
      }

      // Update product status to 'deleted'
      await updateProduct(product.id, session.user.id, { status: 'deleted' });

      toast({
        title: 'Producto eliminado',
        description: 'Tu producto ha sido marcado como eliminado.',
      });
      onUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error al eliminar producto',
        description: error?.message || 'No se pudo eliminar el producto',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [product, session, productImages, toast, onUpdated, onClose]);

  if (!isOpen || !productId) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9999]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                onClose();
              }
            }}
            onTouchStart={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                onClose();
              }
            }}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22 }}
            className="fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[10000] flex min-h-0 flex-col overflow-x-hidden"
            style={{
              top: isDesktop ? `${headerHeight}px` : 0,
              bottom: isDesktop ? undefined : 0,
              height: '100%',
            }}
            data-edit-product-sidebar
          >
            <div className="flex flex-col gap-0 p-4 border-b shrink-0 bg-primary text-primary-foreground">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold flex items-center gap-2 whitespace-nowrap">
                  <Package className="w-5 h-5 shrink-0" />
                  Editar producto
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-primary-foreground hover:bg-primary-foreground/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-4 p-4 pb-0">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Cargando producto...</div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Código de barras</Label>
                      <div className="flex gap-2">
                        <Input
                          value={barcode}
                          onChange={(e) => setBarcode(e.target.value)}
                          placeholder="Código de barras"
                        />
                        <Button type="button" variant="outline" className="px-3" title="Escáner">
                          <ScanLine className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <select
                        value={productType}
                        onChange={(e) => {
                          setProductType(e.target.value);
                          setGroceriesSubcategory('');
                        }}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {productType === 'groceries' && (
                      <div className="space-y-2">
                        <Label>Subcategoría</Label>
                        <select
                          value={groceriesSubcategory}
                          onChange={(e) => setGroceriesSubcategory(e.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="">Selecciona subcategoría</option>
                          {GROCERIES_SUBCATEGORY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-xs">Marca</Label>
                      <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Ej: Minibid Select" />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Nombre del producto</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Galletitas de avena" />
                    </div>

                    {productType === 'groceries' && (
                      <div className="space-y-2">
                        <Label className="text-xs">Contenido por unidad</Label>
                        <div className="flex gap-2">
                          <div className="flex gap-1 flex-1">
                            <Button
                              type="button"
                              variant={quantityKind === 'mass' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => {
                                setQuantityKind('mass');
                                setQuantityUnit('gr');
                              }}
                              className="flex-1"
                            >
                              Peso
                            </Button>
                            <Button
                              type="button"
                              variant={quantityKind === 'liquid' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => {
                                setQuantityKind('liquid');
                                setQuantityUnit('ml');
                              }}
                              className="flex-1"
                            >
                              Líquido
                            </Button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={quantityValue}
                            onChange={(e) => setQuantityValue(e.target.value)}
                            placeholder="Cantidad"
                            className="flex-1"
                          />
                          <div className="flex gap-1">
                            {quantityKind === 'mass' ? (
                              <>
                                <Button
                                  type="button"
                                  variant={quantityUnit === 'gr' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setQuantityUnit('gr')}
                                  className="w-12"
                                >
                                  gr
                                </Button>
                                <Button
                                  type="button"
                                  variant={quantityUnit === 'kg' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setQuantityUnit('kg')}
                                  className="w-12"
                                >
                                  kg
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  type="button"
                                  variant={quantityUnit === 'ml' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setQuantityUnit('ml')}
                                  className="w-12"
                                >
                                  ml
                                </Button>
                                <Button
                                  type="button"
                                  variant={quantityUnit === 'l' ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setQuantityUnit('l')}
                                  className="w-12"
                                >
                                  l
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <RichTextEditor
                        content={description}
                        onChange={setDescription}
                        placeholder="Describe el producto con detalles importantes..."
                      />
                    </div>

                    <div className="space-y-2 border-t pt-4">
                      <Label>Galería de Imágenes</Label>
                      <ImageStage
                        currentImages={productImages}
                        onDragEnd={handleImageDragEnd}
                        onRemoveImage={handleRemoveImageUrl}
                        disabled={false}
                        isUploading={false}
                        onOpenGallery={() => setIsImageManagerOpen(true)}
                      />

                      {session && (
                        <ImageManagerDialog
                          session={session}
                          onSelect={handleImageManagerSelect}
                          isOpen={isImageManagerOpen}
                          onClose={() => setIsImageManagerOpen(false)}
                          allowUpload={true}
                          allowMultipleSelect={true}
                          maxSelection={10}
                          initialSelectedUrls={productImages}
                        />
                      )}
                    </div>

                    {productType === 'vehicle' && (
                      <div className="space-y-2 border-t pt-4">
                        <h3 className="font-semibold text-sm">Detalles del Vehículo</h3>
                        <div className="space-y-2">
                          <Label>Año</Label>
                          <Input type="number" value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} placeholder="Ej: 2020" />
                        </div>
                        <div className="space-y-2">
                          <Label>Kilometraje</Label>
                          <Input type="text" value={vehicleKilometers} onChange={(e) => setVehicleKilometers(e.target.value)} placeholder="Ej: 120000" />
                        </div>
                        <div className="space-y-2">
                          <Label>Tipo de Combustible</Label>
                          <select value={vehicleFuelType} onChange={(e) => setVehicleFuelType(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                            <option value="nafta">Nafta</option>
                            <option value="diesel">Diésel</option>
                            <option value="electrico">Eléctrico</option>
                            <option value="hibrido">Híbrido</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Transmisión</Label>
                          <select value={vehicleTransmission} onChange={(e) => setVehicleTransmission(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                            <option value="manual">Manual</option>
                            <option value="automatica">Automática</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                          <Checkbox id="vehicle-gnc" checked={vehicleGNC} onCheckedChange={(checked) => setVehicleGNC(!!checked)} />
                          <Label htmlFor="vehicle-gnc" className="font-normal cursor-pointer">Tiene GNC</Label>
                        </div>
                      </div>
                    )}

                    {productType === 'electronics' && (
                      <div className="space-y-2 border-t pt-4">
                        <h3 className="font-semibold text-sm">Detalles de Electrónica</h3>
                        <div className="space-y-2">
                          <Label>Almacenamiento</Label>
                          <Input type="text" value={electronicsStorage} onChange={(e) => setElectronicsStorage(e.target.value)} placeholder="Ej: 128GB" />
                        </div>
                        <div className="space-y-2">
                          <Label>RAM</Label>
                          <Input type="text" value={electronicsRAM} onChange={(e) => setElectronicsRAM(e.target.value)} placeholder="Ej: 8GB" />
                        </div>
                      </div>
                    )}

                    <QuickProductForm
                      productType={productType}
                      quantityKind={quantityKind}
                      quantityValue={quantityValue}
                      quantityUnit={quantityUnit}
                      stock={stock}
                      price={price}
                      model={model}
                      size={size}
                      color={color}
                      deliveryByMinibid={deliveryByMinibid}
                      deliveryLocal={deliveryLocal}
                      deliveryAgree={deliveryAgree}
                      onQuantityKindChange={setQuantityKind}
                      onQuantityValueChange={setQuantityValue}
                      onQuantityUnitChange={setQuantityUnit}
                      onStockChange={setStock}
                      onPriceChange={setPrice}
                      onModelChange={productType !== 'vehicle' ? setModel : undefined}
                      onSizeChange={setSize}
                      onColorChange={setColor}
                      onDeliveryByMinibidChange={setDeliveryByMinibid}
                      onDeliveryLocalChange={setDeliveryLocal}
                      onDeliveryAgreeChange={setDeliveryAgree}
                    />

                    <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-card p-4 space-y-3 mt-4">
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-primary uppercase tracking-wide">Editando producto</p>
                          <p className="text-xs text-muted-foreground">Vista previa</p>
                        </div>
                      </div>

                      <ProductPreviewCard
                        images={productImages}
                        title={title}
                        brand={brand}
                        model={model}
                        price={price}
                        stock={stock}
                        description={description}
                        deliveryByMinibid={deliveryByMinibid}
                        deliveryLocal={deliveryLocal}
                        deliveryAgree={deliveryAgree}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="border-t bg-background sticky bottom-0 p-4 flex-shrink-0 mb-4">
              <div className="flex flex-col gap-2">
                <Button 
                  type="button"
                  onClick={handleSubmit} 
                  disabled={saving || loading}
                  className="h-[43px] w-full rounded-full text-sm font-semibold shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75"
                >
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full h-11 font-semibold rounded-full" 
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <button
                  type="button"
                  disabled={deleting || loading}
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar producto
                </button>
              </div>
            </div>

            {/* Delete confirmation modal */}
            <AnimatePresence>
              {showDeleteConfirm && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center"
                  onClick={() => !deleting && setShowDeleteConfirm(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-background rounded-lg shadow-lg p-6 max-w-sm mx-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                          <Trash2 className="h-5 w-5 text-destructive" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base">Eliminar producto</h3>
                          <p className="text-sm text-muted-foreground mt-1">¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.</p>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={deleting}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={handleDeleteProduct}
                          disabled={deleting}
                        >
                          {deleting ? 'Eliminando...' : 'Eliminar'}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
